import { useEffect, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Package } from '@revenuecat/purchases-js'

import { userHasAllGroupedEntitlements } from '@/hooks/useHasActiveSubscription.ts'
import { useOauthSubscriptions } from '@/services/auth.ts'
import { Subscription } from '@/types/Auth'
import { useTrackPaymentFlow } from '../trackingPaymentFlow.ts'
import { paymentSessionContext } from '../ExternalPremiumLogin/lib'

import {
  CreateStripeSessionRequest,
  GetActivePromotionProductsRequest,
  oauthCheckoutSessionCreate,
  oauthGetProductsInPromotion,
} from '@/services/checkout.ts'

import {
  PromotionConfig,
  parsePromotionConfig,
  isResetPromotion,
  isResetExtensionPromotion,
  PROMOTION_OVERRIDE_KEYS,
} from '@/constants/UiPropertyKeys'

import { GenericProductList } from '@/pages/external/components/checkout/GenericProductList'
import { ResetProductPage } from './ResetProductPage'
import { ExistingSubscriberMessage } from './ExistingSubscriberMessage'
import { NotEligibleForPromotionMessage } from './NotEligibleForPromotionMessage'
import { NoOfferingsAvailableMessage } from './NoOfferingsAvailableMessage'
import { TRACKING_TYPES } from '@/constants/TrackingConstants.ts'
import { AppShell, AppButton } from '@/pages/external/components/shell'

import {
  ProductStrategy,
  ProductStrategyFactory
} from './productStrategy'

interface SubscriptionAwareProductFlowProps {
  token: string
  base64ClientKey: string
  promotionIds: string[]
  returnUrl?: string
}

// Product with its associated promotionId for checkout
interface ProductWithPromotion {
  product: import('@/services/checkout').DuprProduct
  promotionId: string
}

enum FlowState {
  LOADING = 'loading',
  FETCHING_INITIAL_DATA = 'fetching_initial_data',
  EVALUATING_ELIGIBILITY = 'evaluating_eligibility',
  EXISTING_SUBSCRIBER = 'existing_subscriber',
  SHOWING_PROMOTIONS = 'showing_promotions',
  NOT_ELIGIBLE_FOR_PROMOTION = 'not_eligible_for_promotion',
  FETCHING_PROMOTION_OFFERING = 'fetching_promotion_offering',
  FETCHING_STANDARD_PRODUCTS = 'fetching_standard_products',
  SHOWING_STANDARD_PRODUCTS = 'showing_standard_products',
  NO_OFFERINGS_AVAILABLE = 'no_offerings_available',
  ERROR = 'error'
}

export function SubscriptionAwareProductFlow({
  token,
  base64ClientKey,
  promotionIds,
  returnUrl,
}: SubscriptionAwareProductFlowProps): JSX.Element {
  const { t } = useTranslation()
  const track = useTrackPaymentFlow()
  const { mutateAsync: getSubscriptions } = useOauthSubscriptions()
  const { revenueCat } = useContext(paymentSessionContext)

  const [flowState, setFlowState] = useState<FlowState>(FlowState.LOADING)
  const [productStrategies, setProductStrategies] = useState<ProductStrategy[]>([])
  const [promotionConfig, setPromotionConfig] = useState<PromotionConfig | null>(null)
  const [fetchedProducts, setFetchedProducts] = useState<ProductWithPromotion[]>([])
  const [localSubscriptions, setLocalSubscriptions] = useState<Subscription[]>([])
  const [hadPromotionProperties, setHadPromotionProperties] = useState(false)
  // When the promotion sets a RevenueCat offering id override, let RC drive
  // which products are shown for this promotion (rendered under promo branding).
  const [promotionOfferingId, setPromotionOfferingId] = useState<string | null>(null)

  useEffect(() => {
    track(TRACKING_TYPES.PAYMENT_STARTED)
  }, [track])

  // Fetch subscriptions and promotional products in parallel
  useEffect(() => {
    if (!token || flowState !== FlowState.LOADING) return

    setFlowState(FlowState.FETCHING_INITIAL_DATA)

    const fetchInitialData = async () => {
      try {
        // Track properties from the first promotion for UI customization
        let firstPromotionProperties: Record<string, string> = {}

        // Create promises for both subscription check and product fetches
        const subscriptionPromise = getSubscriptions({ token })
          .then((res) => {
            setLocalSubscriptions(res.subscriptions ?? [])
            return res
          })
          .catch((error) => {
            console.error('Failed to check subscription status:', error)
            return null // Continue even if subscription check fails
          })

        const productPromises = promotionIds.map(async (promoId, index) => {
          try {
            const result = await oauthGetProductsInPromotion({
              token,
              request: {
                base64ClientKey,
                promotionId: promoId,
              } as GetActivePromotionProductsRequest
            })

            // Capture properties from the first promotion only
            if (index === 0 && result.properties) {
              firstPromotionProperties = result.properties
            }

            // Associate each product with its promotion ID
            return result.products.map(product => ({
              product,
              promotionId: promoId
            })) as ProductWithPromotion[]
          } catch (error) {
            console.error(`Failed to fetch products for promotion ${promoId}:`, error)
            return [] as ProductWithPromotion[]
          }
        })

        // Wait for all fetches to complete in parallel
        const [, ...productResults] = await Promise.all([subscriptionPromise, ...productPromises])

        const allProductsWithPromos = (productResults as ProductWithPromotion[][]).flat()

        // Parse and store promotion config for UI customization
        const config = parsePromotionConfig(firstPromotionProperties)
        setPromotionConfig(config)
        setHadPromotionProperties(Object.keys(firstPromotionProperties).length > 0)
        setPromotionOfferingId(
          firstPromotionProperties[PROMOTION_OVERRIDE_KEYS.RC_OFFERING_ID] ?? null
        )

        // Deduplicate by productId, keeping the first occurrence
        const seenProductIds = new Set<string>()
        const uniqueProducts: ProductWithPromotion[] = []

        for (const productWithPromo of allProductsWithPromos) {
          if (!seenProductIds.has(productWithPromo.product.productId)) {
            seenProductIds.add(productWithPromo.product.productId)
            uniqueProducts.push(productWithPromo)
          }
        }

        // Store fetched products and move to eligibility evaluation
        setFetchedProducts(uniqueProducts)
        setFlowState(FlowState.EVALUATING_ELIGIBILITY)
      } catch (error) {
        console.error('Failed to fetch initial data:', error)

        // Log the specific error for debugging
        if (error && typeof error === 'object' && 'response' in error) {
          const responseError = error as { response?: { status?: number; data?: any } }
          console.error('API Error details:', {
            status: responseError.response?.status,
            data: responseError.response?.data
          })
        }

        // On any error, fall back to standard RevenueCat product flow
        setFlowState(FlowState.FETCHING_STANDARD_PRODUCTS)
      }
    }

    fetchInitialData()
  }, [token, flowState, getSubscriptions, base64ClientKey, promotionIds])

  // Evaluate eligibility after initial data is fetched
  useEffect(() => {
    if (flowState !== FlowState.EVALUATING_ELIGIBILITY) return

    // Check entitlements locally instead of reading from Redux
    const requiredEntitlements = promotionConfig?.entitlementsProvides
    const hasRequiredEntitlements = (!requiredEntitlements || requiredEntitlements.length === 0)
      ? localSubscriptions.some((s) =>
          Object.values(s.entitlements).flat().some((e) => e.includes('PREMIUM'))
        )
      : userHasAllGroupedEntitlements(localSubscriptions, requiredEntitlements)

    // If user has all required entitlements, show existing subscriber message
    if (hasRequiredEntitlements) {
      setFlowState(FlowState.EXISTING_SUBSCRIBER)
      return
    }

    // Offering override: the promotion delegates product selection to RevenueCat.
    // Fetch the named offering and render its packages instead of resolving DUPR
    // products ourselves. Takes precedence over any DUPR products returned.
    if (promotionOfferingId) {
      setFlowState(FlowState.FETCHING_PROMOTION_OFFERING)
      return
    }

    // User is missing at least one required entitlement - show checkout flow
    if (fetchedProducts.length > 0) {
      // Create strategies for DuprProducts, each with its own promotionId
      const duprStrategies = fetchedProducts.map(({ product, promotionId: promoId }) =>
        ProductStrategyFactory.createDuprStrategy(
          product,
          async (stripeRequest: CreateStripeSessionRequest) => {
            const result = await oauthCheckoutSessionCreate({
              token,
              stripeRequest: {
                ...stripeRequest,
                promotionId: promoId,
                base64ClientKey,
                returnUrl,
              },
            })
            return result.clientSecret as string
          }
        )
      )

      setProductStrategies(duprStrategies)
      setFlowState(FlowState.SHOWING_PROMOTIONS)
    } else if (hadPromotionProperties) {
      // Promotion returned properties but no products — user is not eligible
      // for this specific product. Show a dedicated screen rather than
      // silently falling through to standard offerings under promo branding.
      setFlowState(FlowState.NOT_ELIGIBLE_FOR_PROMOTION)
    } else {
      // No promotion at all — fetch standard RevenueCat products
      setFlowState(FlowState.FETCHING_STANDARD_PRODUCTS)
    }
  }, [flowState, localSubscriptions, fetchedProducts, promotionConfig, hadPromotionProperties, promotionOfferingId, token, base64ClientKey])

  // Fetch the promotion's RevenueCat offering override and build strategies from
  // its packages. Mirrors the standard-products fetch below, but targets the
  // specific offering named by the promotion and keeps the promotion config so
  // the picker still renders under promo branding.
  useEffect(() => {
    if (flowState !== FlowState.FETCHING_PROMOTION_OFFERING || !revenueCat || !promotionOfferingId) return

    const fetchPromotionOffering = async () => {
      try {
        const offerings = await revenueCat.getOfferings()
        const offering = offerings.all[promotionOfferingId] ?? null
        const packages = offering?.availablePackages ?? []

        if (packages.length > 0) {
          const revenueCatStrategies = packages.map((pkg: Package) =>
            ProductStrategyFactory.createRevenueCatStrategy(
              pkg,
              revenueCat,
              () => {
                // Handle successful RevenueCat purchase
              }
            )
          )

          setProductStrategies(revenueCatStrategies)
          setFlowState(FlowState.SHOWING_PROMOTIONS)
        } else {
          // Promotion names an offering that has no packages — treat as a
          // misconfiguration rather than silently falling back to standard
          // offerings under promo branding.
          setFlowState(FlowState.NO_OFFERINGS_AVAILABLE)
        }
      } catch (error) {
        console.error('Failed to fetch promotion RevenueCat offering:', error)
        setFlowState(FlowState.ERROR)
      }
    }

    fetchPromotionOffering()
  }, [flowState, revenueCat, promotionOfferingId])

  // Fetch RevenueCat products
  useEffect(() => {
    if (flowState !== FlowState.FETCHING_STANDARD_PRODUCTS || !revenueCat) return

    // RC offerings are Dupr+ subscriptions — if the user already has PREMIUM,
    // there is nothing here to sell them. Skip the RC fetch entirely.
    const hasPremium = localSubscriptions.some((s) =>
      Object.values(s.entitlements).flat().some((e) => e.includes('PREMIUM'))
    )
    if (hasPremium) {
      setFlowState(FlowState.NO_OFFERINGS_AVAILABLE)
      return
    }

    const fetchRevenueCatProducts = async () => {
      try {
        const offerings = await revenueCat.getOfferings()

        if (offerings.current && offerings.current.availablePackages.length > 0) {
          // Create strategies for RevenueCat packages
          const revenueCatStrategies = offerings.current.availablePackages.map((pkg: Package) =>
            ProductStrategyFactory.createRevenueCatStrategy(
              pkg,
              revenueCat,
              () => {
                // Handle successful RevenueCat purchase
              }
            )
          )

          setProductStrategies(revenueCatStrategies)
          setFlowState(FlowState.SHOWING_STANDARD_PRODUCTS)
        } else {
          setFlowState(FlowState.NO_OFFERINGS_AVAILABLE)
        }
      } catch (error) {
        console.error('Failed to fetch RevenueCat products:', error)
        setFlowState(FlowState.ERROR)
      }
    }

    fetchRevenueCatProducts()
  }, [flowState, revenueCat, localSubscriptions])

  // Render based on flow state
  switch (flowState) {
    case FlowState.LOADING:
    case FlowState.FETCHING_INITIAL_DATA:
    case FlowState.EVALUATING_ELIGIBILITY:
    case FlowState.FETCHING_PROMOTION_OFFERING:
    case FlowState.FETCHING_STANDARD_PRODUCTS:
      return (
        <AppShell logoLinksToDashboard={false}>
          <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--accent)] mx-auto mb-4" />
              <p className="text-[color:var(--ink-soft)]">
                {flowState === FlowState.FETCHING_INITIAL_DATA || flowState === FlowState.EVALUATING_ELIGIBILITY
                  ? t('subscription.checking', 'Checking subscription status...')
                  : flowState === FlowState.FETCHING_STANDARD_PRODUCTS || flowState === FlowState.FETCHING_PROMOTION_OFFERING
                  ? t('products.loadingStandard', 'Loading subscription options...')
                  : t('common.loading', 'Loading...')
                }
              </p>
            </div>
          </div>
        </AppShell>
      )

    case FlowState.EXISTING_SUBSCRIBER:
      return (
        <ExistingSubscriberMessage
          benefits={promotionConfig?.benefits}
          productName={fetchedProducts.length === 1 ? fetchedProducts[0].product.displayName : undefined}
        />
      )

    case FlowState.SHOWING_PROMOTIONS: {
      const isOriginalReset = isResetPromotion(promotionConfig)
      const isResetExtension = isResetExtensionPromotion(promotionConfig)
      // The reset page assumes DUPR/Stripe strategies; skip it when RC is driving
      // the products via an offering override.
      if (!promotionOfferingId && (isOriginalReset || isResetExtension)) {
        return (
          <ResetProductPage
            productStrategies={productStrategies}
            base64ClientKey={base64ClientKey}
            promotionId={promotionIds[0]}
            promotionConfig={promotionConfig ?? undefined}
            variant={isResetExtension ? 'extension' : 'original'}
          />
        )
      }
      return (
        <GenericProductList
          productStrategies={productStrategies}
          base64ClientKey={base64ClientKey}
          promotionId={promotionIds[0]}
          promotionConfig={promotionConfig ?? undefined}
          onProductSelect={(strategy, _index) => {
            strategy.getTrackingData()
          }}
        />
      )
    }

    case FlowState.SHOWING_STANDARD_PRODUCTS:
      // No promo products are being shown here — drop promotionConfig so the
      // picker uses UI defaults for title / instructions / benefits / footer
      // / CTA rather than the (now-irrelevant) promotion's properties.
      return (
        <GenericProductList
          productStrategies={productStrategies}
          base64ClientKey={base64ClientKey}
          promotionId={promotionIds[0]}
          onProductSelect={(strategy, _index) => {
            strategy.getTrackingData()
          }}
        />
      )

    case FlowState.NOT_ELIGIBLE_FOR_PROMOTION:
      return (
        <NotEligibleForPromotionMessage
          onSeeOtherOfferings={() => setFlowState(FlowState.FETCHING_STANDARD_PRODUCTS)}
        />
      )

    case FlowState.NO_OFFERINGS_AVAILABLE:
      return <NoOfferingsAvailableMessage />

    case FlowState.ERROR:
      return (
        <AppShell logoLinksToDashboard={false}>
          <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
            <div className="text-center max-w-md mx-auto p-6">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[color:var(--negative)]/10 flex items-center justify-center">
                <svg className="w-6 h-6 text-[color:var(--negative)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] text-[color:var(--ink)] mb-2">
                {t('errors.noProductsAvailable', 'No Products Available')}
              </h2>
              <p className="text-[color:var(--ink-soft)] mb-6">
                {t('errors.productLoadFailed', 'Failed to load subscription options. Please try again later.')}
              </p>
              <AppButton onClick={() => window.location.reload()}>
                {t('common.tryAgain', 'Try Again')}
              </AppButton>
            </div>
          </div>
        </AppShell>
      )

    default:
      return (
        <AppShell logoLinksToDashboard={false}>
          <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
            <p className="text-[color:var(--ink-soft)]">{t('errors.unexpectedState', 'Unexpected state')}</p>
          </div>
        </AppShell>
      )
  }
}

export default SubscriptionAwareProductFlow
